import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from './ui';

/**
 * Signature capture (SPEC §5). The mark is drawn or uploaded here, sent once,
 * and never kept on this side either: there is no "signature on file", so
 * every signing event needs a fresh one.
 */
export function SignatureDialog({
  open,
  title,
  attestation,
  confirmLabel,
  signerName,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  attestation: string;
  confirmLabel: string;
  signerName: string;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (dataUri: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasMark, setHasMark] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const prepare = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#12211f';
    // White rather than transparent: the mark is composited onto a page.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  useEffect(() => {
    if (!open) return;
    setHasMark(false);
    setUploadError(null);
    const id = requestAnimationFrame(prepare);
    return () => cancelAnimationFrame(id);
  }, [open, prepare]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  function pointFrom(event: React.PointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointFrom(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointFrom(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasMark(true);
  }

  function end() {
    drawing.current = false;
  }

  function clear() {
    prepare();
    setHasMark(false);
    setUploadError(null);
  }

  async function upload(file: File) {
    setUploadError(null);
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setUploadError('Upload a PNG or JPEG image of your signature.');
      return;
    }
    if (file.size > 400 * 1024) {
      setUploadError('That image is larger than 400 KB.');
      return;
    }
    const url = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('unreadable'));
        img.src = url;
      });
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      prepare();
      const rect = canvas.getBoundingClientRect();
      const scale = Math.min(rect.width / image.width, rect.height / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      ctx.drawImage(image, (rect.width - width) / 2, (rect.height - height) / 2, width, height);
      setHasMark(true);
    } catch {
      setUploadError('That image could not be read.');
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function confirm() {
    const canvas = canvasRef.current;
    if (!canvas || !hasMark) return;
    onConfirm(canvas.toDataURL('image/png'));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{attestation}</p>

        {error && (
          <div className="mt-4">
            <Alert>{error}</Alert>
          </div>
        )}
        {uploadError && (
          <div className="mt-4">
            <Alert tone="warning">{uploadError}</Alert>
          </div>
        )}

        <div className="mt-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Sign below</span>
            <button
              type="button"
              onClick={clear}
              className="text-xs text-slate-500 underline underline-offset-4 hover:text-slate-800 dark:text-slate-400"
            >
              Clear
            </button>
          </div>

          <canvas
            ref={canvasRef}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            className="mt-1.5 h-40 w-full touch-none rounded-lg border border-slate-300 bg-white dark:border-slate-700"
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {signerName} · {new Date().toLocaleString()}
            </p>
            <label className="cursor-pointer text-xs text-brand-700 underline underline-offset-4 dark:text-brand-300">
              Upload an image instead
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void upload(file);
                }}
              />
            </label>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!hasMark || busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          Your signature is placed on the signed record and kept nowhere else: there is no signature on file, so
          you will be asked to sign again next time.
        </p>
      </div>
    </div>
  );
}
